import { useTranslation } from 'react-i18next'

import { createNewsArticleSourceChip } from '@/features/actualidad-hostelera/NewsArticleSourceChip'
import { newsArticlePath, type NewsArticleBodyProps } from '@/features/actualidad-hostelera/articles'
import { LocaleLink } from '@/navigation/LocaleLink'
import { cn } from '@/lib/utils'
import { article } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import '@/styles/food-cost-fonts.css'

const k = (f: string) => `news.articles.digitalizacion-hosteleria-la-empuja-el-boe.${f}`

const SEGITTUR_HREF =
    'https://www.segittur.es/transformacion-digital/proyectos-transformacion-digital/plan-de-activacion-digital-del-sector-horeca-turistico/index.html'
const AEAT_VERIFACTU_HREF =
    'https://sede.agenciatributaria.gob.es/Sede/iva/sistemas-informaticos-facturacion-verifactu/nota-informativa-ampliacion-plazo-adaptacion-facturacion.html'
const BOE_RD_1007_HREF = 'https://www.boe.es/buscar/act.php?id=BOE-A-2023-24840'

const REF = {
    segittur: {
        href: SEGITTUR_HREF,
        domain: 'segittur.es',
        label: 'SEGITTUR',
    },
    aeat: {
        href: AEAT_VERIFACTU_HREF,
        domain: 'agenciatributaria.gob.es',
        label: 'AEAT',
    },
    boe: {
        href: BOE_RD_1007_HREF,
        domain: 'boe.es',
        label: 'BOE · RD 1007/2023',
    },
} as const

const SourceChip = createNewsArticleSourceChip(REF, k)

export function DigitalizacionBoeTribunaArticle({ article: meta }: NewsArticleBodyProps) {
    const { t } = useTranslation()

    const sources = [
        {
            label: t(
                k('sources.1'),
                'Plan de Activación Digital del sector HORECA turístico e informe «La digitalización en el canal HORECA turístico en 2026», SEGITTUR y Hostelería de España, con EY, publicado el 18 de junio de 2026',
            ),
            href: REF.segittur.href,
        },
        {
            label: t(
                k('sources.2'),
                'Nota informativa AEAT: ampliación del plazo de adaptación de los sistemas informáticos de facturación (RDL 15/2025)',
            ),
            href: REF.aeat.href,
        },
        {
            label: t(
                k('sources.3'),
                'Real Decreto 1007/2023, reglamento de sistemas informáticos de facturación (Verifactu), BOE',
            ),
            href: REF.boe.href,
        },
    ]

    return (
        <div className={article.page}>
            <article className={article.shell}>
                <ArticleCoverHero
                    category={meta.category}
                    title={meta.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'Verifactu llega en 2027. La digitalización de la hostelería no la mueve el retorno: la mueve la obligación. Opinión a partir del estudio SEGITTUR y Hostelería de España.',
                            )}
                        </>
                    }
                    imageSrc={meta.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'TPV y papeles de facturación en la barra de un bar, sin nadie mirando los informes',
                    )}
                    byline={
                        <ArticleByline
                            authorName={meta.authorName}
                            readingMinutes={meta.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <div className={article.proseIntro}>
                    <p className={cn(article.p, article.pGapMd)}>
                        <span className={article.em}>
                            {t(
                                k('lead'),
                                'El 1 de enero de 2027 entran las sociedades. El 1 de julio, los autónomos. Miles de bares y restaurantes van a cambiar de programa de facturación en esos meses. Ninguno lo hará porque haya calculado el retorno.',
                            )}
                        </span>
                    </p>
                    <p className={cn(article.p, article.pGapMd)}>
                        {t(k('intro.1Before'), 'Lo harán porque toca, por el ')}
                        <a href={BOE_RD_1007_HREF} target='_blank' rel='noopener noreferrer' className={article.link}>
                            {t(k('intro.1Link'), 'Real Decreto 1007/2023')}
                        </a>
                        {t(
                            k('intro.1After'),
                            ', aplazado un año por el Real Decreto-ley 15/2025. Y esa frase, «porque toca», describe mejor que cualquier plan cómo se está digitalizando la hostelería española.',
                        )}
                        <SourceChip source='aeat' />
                    </p>
                </div>

                <section id='patron'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.patron'), 'No es una anécdota, es el patrón')}
                    </h2>
                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(
                                k('p.patron.1'),
                                'Repasa los últimos quince años del sector y busca un salto tecnológico que no tenga una fecha oficial detrás.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(
                                k('p.patron.2'),
                                'El TPV moderno entró con la trazabilidad fiscal. La carta digital entró con la pandemia. La gestión de envases entrará con el sistema de depósito y retorno. Cada vez que este sector ha adoptado tecnología en masa, la decisión no la tomó el hostelero: la tomó un boletín, una inspección o un cliente que ya no aceptaba otra cosa.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(
                                k('p.patron.3Before'),
                                'El estudio sobre digitalización del canal HORECA turístico que Hostelería de España y SEGITTUR publicaron en junio lo dice con otras palabras, y es su conclusión más incómoda para los que estamos en este lado: ',
                            )}
                            <span className={article.em}>
                                {t(
                                    k('p.patron.3Em'),
                                    'la digitalización del canal no depende únicamente del hostelero, sino también de los agentes que le rodean en su día a día.',
                                )}
                            </span>
                            <SourceChip source='segittur' />
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(
                                k('p.patron.4'),
                                'El informe nombra cinco: empresas tecnológicas, partners y proveedores, asociaciones y gremios, entidades formativas y asesores externos.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(
                                k('p.patron.5'),
                                'Yo añadiría un sexto empujón, que no es un agente pero es el que pone las fechas: la norma.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('p.patron.6'),
                                'Y conviene ver lo que eso significa. La obligación es lo único en este sector que mueve a todo el mundo a la vez, el mismo mes, con presupuesto aprobado y sin discusión. Ninguna campaña comercial consigue eso. Es la palanca de adopción más potente que existe en hostelería, y la estamos gastando entera en cumplir.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='minimo'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.minimo'), 'El problema de digitalizar a la fuerza')}
                    </h2>
                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(
                                k('p.minimo.1'),
                                'Esto no es una queja sobre la regulación. Verifactu tiene sentido y el sector lo va a absorber, como absorbió todo lo anterior.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(k('p.minimo.2Before'), 'El problema es otro: ')}
                            <span className={article.em}>
                                {t(
                                    k('p.minimo.2Em'),
                                    'cuando la tecnología entra por obligación, entra al mínimo.',
                                )}
                            </span>
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(
                                k('p.minimo.3'),
                                'Un hostelero que cambia de software porque se lo exige Hacienda compra lo que cumple. No compra lo que le sirve, porque no está tomando una decisión de negocio, está cerrando un frente. Firma, lo instala, y vuelve a la cocina.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(
                                k('p.minimo.4'),
                                'El resultado es un parque instalado enorme que produce datos que nadie lee.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(
                                k('p.minimo.5Before'),
                                'Y ahí está el dato del estudio que más me interesa, y que casi nadie ha citado: ',
                            )}
                            <span className={article.em}>
                                {t(
                                    k('p.minimo.5Em'),
                                    'el 73% de los hosteleros dice que la digitalización no le ha ahorrado ni la mitad del tiempo que dedica a la gestión',
                                )}
                            </span>
                            {t(k('p.minimo.5After'), ', según datos de TheFork recogidos en el informe.')}
                            <SourceChip source='segittur' />
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(
                                k('p.minimo.6'),
                                'Eso no es un fracaso del hostelero. Es la firma de una adopción defensiva. Si instalas para cumplir, ahorrar tiempo nunca fue el objetivo.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('p.minimo.7'),
                                'El propio estudio recoge la frase de uno de los hosteleros entrevistados: «no es falta de interés, es falta de tiempo y acompañamiento».',
                            )}
                        </p>
                    </div>
                </section>

                <section id='distinto'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.distinto'), 'Tres cosas que haría distinto')}
                    </h2>
                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(
                                k('p.distinto.1'),
                                'Si el estudio reparte la responsabilidad entre cinco agentes, conviene que cada uno diga qué haría distinto. Esto es lo que yo haría desde el mío.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            <span className={article.em}>
                                {t(
                                    k('p.distinto.2Em'),
                                    'Primero: tratar la fecha obligatoria como puerta de entrada, no como venta cerrada.',
                                )}
                            </span>{' '}
                            {t(
                                k('p.distinto.2'),
                                'El día que un hostelero cambia de sistema por Verifactu es el único día del año en el que tiene las manos metidas dentro. Es el mejor momento posible para enseñarle qué más puede hacer ese dato, y es justo el momento en el que el sector le cobra y se va.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            <span className={article.em}>
                                {t(
                                    k('p.distinto.3Em'),
                                    'Segundo: medir la adopción por uso, no por instalación.',
                                )}
                            </span>{' '}
                            {t(
                                k('p.distinto.3'),
                                'Las cifras de digitalización que maneja el sector cuentan licencias activas. Contar cuántos hosteleros abren el informe que esas licencias generan daría un número mucho peor y mucho más útil.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            <span className={article.em}>
                                {t(
                                    k('p.distinto.4Em'),
                                    'Tercero: acompañamiento en lugar de formación.',
                                )}
                            </span>{' '}
                            {t(
                                k('p.distinto.4'),
                                'Un webinar de una hora no compite con un servicio de comidas. El estudio de SEGITTUR y Hostelería de España encontró lo que encontró porque hizo 18 acompañamientos en el día a día del negocio, además de 49 entrevistas y 6 focus groups. Esa es la vía, y es cara, y por eso casi nadie la ofrece.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='siguiente'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.siguiente'), 'La siguiente fecha ya está puesta')}
                    </h2>
                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(
                                k('p.siguiente.1'),
                                'La factura electrónica obligatoria entre empresas todavía no tiene fecha de entrada en vigor, pero la va a tener. Y cuando la tenga, este sector se volverá a digitalizar en bloque, otra vez por obligación y otra vez al mínimo necesario.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(
                                k('p.siguiente.2'),
                                'La diferencia entre que eso sea un gasto o sea el principio de algo no la va a marcar la norma. La vamos a marcar los que estamos alrededor del hostelero cuando llega la carta.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(
                                k('p.siguiente.3'),
                                'El propio estudio lo cierra mejor de lo que yo sabría: cuando cada agente entiende su rol y actúa con sentido, la adopción digital se vuelve real.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('p.siguiente.4Before'),
                                'Si lo que te interesa es el contraste entre el TPV que ya aceptaste y el control de costes que sigue fuera, lo contamos con más detalle en ',
                            )}
                            <LocaleLink
                                to={newsArticlePath('por-que-hosteleria-tarda-digitalizarse-control-costes')}
                                className={article.link}
                            >
                                {t(
                                    k('p.siguiente.4Link'),
                                    'por qué la hostelería tarda tanto en digitalizarse',
                                )}
                            </LocaleLink>
                            {t(k('p.siguiente.4After'), '.')}
                        </p>
                    </div>
                </section>

                <section id='interes'>
                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGap2xl)}>
                            <em>
                                {t(
                                    k('bio'),
                                    'Samuel Carrillo es cofundador de Tipi, empresa de control de costes para hostelería independiente. Tipi figura entre las empresas tecnológicas colaboradoras del estudio de digitalización del canal HORECA turístico de Hostelería de España y SEGITTUR, lo que hace de esta tribuna una opinión interesada, y por eso no propone ninguna herramienta.',
                                )}
                            </em>
                        </p>
                    </div>
                </section>

                <section className={article.cta}>
                    <h2 className={cn(article.h2, article.h2Cta)}>
                        {t(k('h2.cta'), 'Cuando toque cambiar de sistema, ¿vas a cumplir o a aprovechar?')}
                    </h2>
                    <p className={article.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'La norma pone la fecha. Lo que hagas con el dato después es otra historia. Si quieres mirarlo sin presión comercial, cuéntanoslo.',
                        )}
                    </p>
                    <div className={article.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(article.btn, article.btnSolid)}>
                            {t(k('cta.button'), 'Hablar con Tipi')}
                        </LocaleLink>
                    </div>
                </section>

                <section className={article.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={article.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={article.sourcesList}>
                        {sources.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={article.link}
                                >
                                    {source.label}
                                </a>
                            </li>
                        ))}
                        <li>
                            {t(
                                k('sources.4'),
                                'Datos de ahorro de tiempo (73%) y cita sobre tiempo y acompañamiento: TheFork / hosteleros entrevistados, recogidos en el informe SEGITTUR y Hostelería de España (páginas 11 y 36 del PDF)',
                            )}
                        </li>
                        <li>
                            {t(
                                k('sources.5'),
                                'Metodología del estudio: 49 entrevistas, 6 focus groups, 18 acompañamientos, 97 agentes del canal (página 8). Cinco agentes del ecosistema: infografía «El hostelero no puede avanzar solo» (página 53)',
                            )}
                        </li>
                    </ul>
                </section>
            </article>
        </div>
    )
}
